import { useEffect, useState } from 'react';
import api from '../services/ApiService';

const PER_PAGE = 15;

const DIR_ICON  = { inbound: '↙', outbound: '↗' };
const DIR_COLOR = { inbound: '#2563eb', outbound: '#16a34a' };
const STATUS_COLOR = { ended: '#6b7280', failed: '#dc2626', started: '#f59e0b', connected: '#16a34a' };

function fmtDate(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(); } catch { return v; }
}

function fmtDur(s) {
  if (!s) return '—';
  const m = Math.floor(s / 60).toString().padStart(2, '0');
  return `${m}:${(s % 60).toString().padStart(2, '0')}`;
}

export default function HistoryPanel({ onDial, supervisorMode = false }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = async () => {
    try {
      const h = supervisorMode
        ? await api.getSupervisorCallHistory()
        : await api.getCallHistory();
      setHistory(h || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const total = Math.max(1, Math.ceil(history.length / PER_PAGE));
  const paged = history.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const dialNumber = (h) => {
    const num = h.callee_number || h.caller_number || '';
    if (num && onDial) onDial(num);
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <div className="admin-page-title">Recent Calls</div>
          <div className="admin-page-subtitle">
            {history.length} records{supervisorMode ? ' · all agents · last 24 hours' : ''}
          </div>
        </div>
        <button className="secondary-btn small-btn" onClick={load}>↻ Refresh</button>
      </div>

      {loading && <p className="admin-muted">Loading…</p>}

      {!loading && history.length === 0 && (
        <div className="content-card" style={{ textAlign: 'center', padding: '48px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📞</div>
          <p className="admin-muted">No call history yet.</p>
        </div>
      )}

      {!loading && paged.map(h => {
        const num = h.callee_number || h.caller_number || '—';
        const dir = h.direction || 'outbound';
        return (
          <div
            key={h.id}
            style={{
              background: 'white',
              borderRadius: '14px',
              padding: '14px 18px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              cursor: 'pointer',
            }}
            onClick={() => dialNumber(h)}
            title="Click to dial this number"
          >
            {/* Direction icon */}
            <div style={{
              width: 38, height: 38, borderRadius: '50%',
              background: dir === 'inbound' ? '#eff6ff' : '#f0fdf4',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '18px', color: DIR_COLOR[dir] || '#6b7280', flexShrink: 0,
            }}>
              {DIR_ICON[dir] || '↕'}
            </div>

            {/* Number + meta */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '15px', color: '#111827' }}>{num}</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                {fmtDate(h.call_start)}
                {h.caller_number && h.direction === 'inbound' && ` · from ${h.caller_number}`}
                {supervisorMode && h.username && (
                  <span style={{ marginLeft: '6px', color: '#6366f1', fontWeight: 600 }}>· {h.username}</span>
                )}
              </div>
            </div>

            {/* Duration */}
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                {fmtDur(h.duration)}
              </div>
              <div style={{
                marginTop: '3px',
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 700,
                background: '#f3f4f6',
                color: STATUS_COLOR[h.status] || '#6b7280',
              }}>
                {h.status || '—'}
              </div>
            </div>

            {/* Dial button */}
            <button
              onClick={(e) => { e.stopPropagation(); dialNumber(h); }}
              style={{
                background: '#2563eb', color: 'white',
                border: 'none', borderRadius: '8px',
                padding: '7px 14px', fontSize: '13px', fontWeight: 600,
                flexShrink: 0,
              }}
            >
              ☎ Dial
            </button>
          </div>
        );
      })}

      {history.length > PER_PAGE && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
          <button
            className="secondary-btn small-btn"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            ← Prev
          </button>
          <span className="admin-muted">Page {page} of {total}</span>
          <button
            className="secondary-btn small-btn"
            disabled={page >= total}
            onClick={() => setPage(p => p + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
